/**
 * Once-per-room request gating, shared by the two surfaces that ask the server
 * for a catalog on join: `useCrafting` (getRecipes) and `GmEventManager`
 * (gmEventTemplateList).
 *
 * Both previously stamped their guard ref on a successful SEND and treated
 * that as done. A send is not an answer. When the server-side handler is still
 * rebinding as the request lands — the ordinary reconnect edge — the reply
 * never arrives, the 500ms poll early-returns forever because the room and
 * session still match, and the window stays empty until the player changes
 * room. The guard now clears itself when an unanswered request outlives the
 * retry window, and only a received reply settles it for good.
 */

export interface RoomRequestRecord {
  /** The Room instance the request was sent on. */
  room: unknown;
  /** Session id at send time — a rekey must re-ask. */
  sessionId: string;
  /** When the request went out, for the retry window. */
  sentAt: number;
  /** Set only when the server's reply actually arrived. */
  answered: boolean;
}

/**
 * Should we (re)send the room request now?
 *
 * @param record  what we last sent, or null if nothing yet
 * @param room    the current Room instance
 * @param sessionId  the current session id
 * @param now     current epoch ms
 * @param retryAfterMs  how long an unanswered request may stay in flight
 */
export function shouldRequestRoomData(
  record: RoomRequestRecord | null,
  room: unknown,
  sessionId: string,
  now: number,
  retryAfterMs: number,
): boolean {
  if (record === null) return true;
  // A different room or a rekeyed session is a different question entirely.
  if (record.room !== room || record.sessionId !== sessionId) return true;
  if (record.answered) return false;
  return now - record.sentAt >= retryAfterMs;
}

/** Default grace period before an unanswered request is retried. */
export const ROOM_REQUEST_RETRY_MS = 1500;
